'use client';

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
  ReactNode,
} from 'react';
import { tours } from '@/config/tours';
import { useSession } from './session-context';
import { fetchApi } from '@/utils/api-client';
import { API_ROUTES } from '@/config/api-routes';

interface TourProgress {
  completedTours: string[];
  seenTours: string[];
  firstVisit: boolean;
}

interface TourContextType {
  startTour: (tourId: string) => void;
  stopTour: () => void;
  isTourCompleted: (tourId: string) => boolean;
  markTourCompleted: (tourId: string) => void;
  resetTourProgress: (tourId?: string) => void;
  shouldShowTour: (tourId: string) => boolean;
  isReady: boolean;
  isRunning: boolean;
}

const TourContext = createContext<TourContextType | null>(null);

const TOUR_STORAGE_KEY = 'nutricrm-tour-progress';
const TOUR_SESSION_KEY = 'nutricrm-tour-session';

const DEFAULT_PROGRESS: TourProgress = {
  completedTours: [],
  seenTours: [],
  firstVisit: true,
};

function migrateProgress(stored: Partial<TourProgress>): TourProgress {
  return {
    completedTours: stored.completedTours ?? [],
    seenTours: stored.seenTours ?? [],
    firstVisit: stored.firstVisit ?? true,
  };
}

function getStoredProgress(): TourProgress {
  try {
    const stored = localStorage.getItem(TOUR_STORAGE_KEY);
    if (stored) {
      return migrateProgress(JSON.parse(stored));
    }
  } catch {
    // localStorage not available or failed
  }
  try {
    const sessionStored = sessionStorage.getItem(TOUR_SESSION_KEY);
    if (sessionStored) {
      return migrateProgress(JSON.parse(sessionStored));
    }
  } catch {
    // sessionStorage not available
  }
  return DEFAULT_PROGRESS;
}

function saveProgress(progress: TourProgress): void {
  try {
    localStorage.setItem(TOUR_STORAGE_KEY, JSON.stringify(progress));
  } catch {
    // localStorage failed (Safari private browsing, quota exceeded)
  }
  try {
    sessionStorage.setItem(TOUR_SESSION_KEY, JSON.stringify(progress));
  } catch {
    // sessionStorage also unavailable
  }
}

export function TourProvider({ children }: { children: ReactNode }) {
  const [progress, setProgress] = useState<TourProgress>(DEFAULT_PROGRESS);
  const [isReady, setIsReady] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [mounted, setMounted] = useState(false);
  const driverRef = useRef<ReturnType<
    typeof import('driver.js').driver
  > | null>(null);
  const currentTourIdRef = useRef<string | null>(null);
  const { userPreferences, isAuthenticated, setUserPreferences } = useSession();
  const backendSyncedRef = useRef(false);

  useEffect(() => {
    setMounted(true);
    const stored = getStoredProgress();

    if (isAuthenticated && userPreferences?.tourCompleted) {
      const newProgress: TourProgress = {
        ...stored,
        firstVisit: false,
      };
      setProgress(newProgress);
      saveProgress(newProgress);
      backendSyncedRef.current = true;
    } else {
      setProgress(stored);
    }

    setIsReady(true);
  }, [isAuthenticated, userPreferences?.tourCompleted]);

  const markTourCompleted = useCallback(
    async (tourId: string) => {
      const newProgress: TourProgress = {
        completedTours: [...new Set([...progress.completedTours, tourId])],
        seenTours: [...new Set([...progress.seenTours, tourId])],
        firstVisit: false,
      };
      setProgress(newProgress);
      saveProgress(newProgress);

      if (isAuthenticated && !backendSyncedRef.current) {
        try {
          await fetchApi(API_ROUTES.USERS.COMPLETE_TOUR, { method: 'PUT' });
          backendSyncedRef.current = true;
          if (userPreferences) {
            setUserPreferences({ ...userPreferences, tourCompleted: true });
          }
        } catch {
          // Silently fail - local storage still tracks progress
        }
      }
    },
    [
      progress.completedTours,
      progress.seenTours,
      isAuthenticated,
      userPreferences,
      setUserPreferences,
    ],
  );

  const stopTour = useCallback(() => {
    try {
      if (driverRef.current) {
        driverRef.current.destroy();
        driverRef.current = null;
      }
    } catch {
      // Driver might not be initialized
    }
    currentTourIdRef.current = null;
    setIsRunning(false);
  }, []);

  const startTour = useCallback(
    async (tourId: string) => {
      const tourConfig = tours[tourId];
      if (!tourConfig || !mounted) return;

      if (driverRef.current) {
        driverRef.current.destroy();
      }

      currentTourIdRef.current = tourId;
      setIsRunning(true);

      const { driver } = await import('driver.js');

      const driverObj = driver({
        showProgress: true,
        animate: true,
        allowClose: true,
        overlayColor: 'rgba(0, 0, 0, 0.5)',
        stagePadding: 8,
        stageRadius: 8,
        popoverClass: 'driver-popover-theme',
        steps: tourConfig.steps,
        onDestroyStarted: () => {
          markTourCompleted(tourId);
          driverObj.destroy();
          currentTourIdRef.current = null;
          setIsRunning(false);
        },
        onDestroyed: () => {
          currentTourIdRef.current = null;
          setIsRunning(false);
        },
      });

      driverRef.current = driverObj;
      driverObj.drive();
    },
    [markTourCompleted, mounted],
  );

  const isTourCompleted = useCallback(
    (tourId: string) => {
      return progress.completedTours.includes(tourId);
    },
    [progress.completedTours],
  );

  const resetTourProgress = useCallback(
    (tourId?: string) => {
      if (tourId) {
        const newProgress: TourProgress = {
          ...progress,
          completedTours: progress.completedTours.filter((t) => t !== tourId),
          seenTours: progress.seenTours.filter((t) => t !== tourId),
        };
        setProgress(newProgress);
        saveProgress(newProgress);
      } else {
        const newProgress: TourProgress = {
          completedTours: [],
          seenTours: [],
          firstVisit: true,
        };
        setProgress(newProgress);
        saveProgress(newProgress);
      }
    },
    [progress],
  );

  const shouldShowTour = useCallback(
    (tourId: string) => {
      if (!isReady) return false;
      return (
        progress.firstVisit &&
        !progress.completedTours.includes(tourId) &&
        !progress.seenTours.includes(tourId)
      );
    },
    [isReady, progress.firstVisit, progress.completedTours, progress.seenTours],
  );

  useEffect(() => {
    return () => {
      try {
        if (driverRef.current) {
          driverRef.current.destroy();
        }
      } catch {
        // Driver might not be initialized
      }
    };
  }, []);

  const contextValue = useMemo(
    () => ({
      startTour,
      stopTour,
      isTourCompleted,
      markTourCompleted,
      resetTourProgress,
      shouldShowTour,
      isReady,
      isRunning,
    }),
    [
      startTour,
      stopTour,
      isTourCompleted,
      markTourCompleted,
      resetTourProgress,
      shouldShowTour,
      isReady,
      isRunning,
    ],
  );

  return (
    <TourContext.Provider value={contextValue}>{children}</TourContext.Provider>
  );
}

export function useTour() {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
}
